import { Col, List, Row } from "antd";
import { BatchModal } from "src/containers/myAccount/BatchModal";
import { NotificationDetails } from "src/containers/myAccount/constants";
import { NotificationBlockedMessage } from "src/containers/myAccount/NotificationBlockedMessage";
import {
  Blank,
  Box,
  DownChevron,
  Dropdown,
  UneditableNotificationOption,
} from "src/containers/myAccount/styles";
import { SessionType } from "src/modules/interface";
import {
  IUser,
  NotificationSelectorType,
  TypeGranularControl,
  TypeUserInfo,
} from "src/modules/myAccount/types";
import { configureAlertSettings, setFlags, updateNotification } from "src/modules/myAccount/utils";
import { memo, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RateNegotiationConfigEnum } from "src/components/RateNegotiationModal/interfaces";
import { useRateNegotiationFlag } from "src/hooks/useRateNegotiationFlag";
import { useRestrictedSignedProfile } from "src/hooks/useRestrictedSignedProfile";
import { noop } from "lodash";
import { useFlags } from "launchdarkly-react-client-sdk";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { useGetInterviewSettings } from "@src/appV2/InterviewSettings/api/useGetInterviewSettings";
import { isDefined } from "@clipboard-health/util-ts";

const uneditableOption = (text: string) => (
  <UneditableNotificationOption data-testid="uneditable-notification-option">
    {text}
  </UneditableNotificationOption>
);

export function createNotifyRoles(
  userInfo: Partial<TypeUserInfo>,
  granularControl?: TypeGranularControl
) {
  let alertSettings;
  const notifySettings = { EMAIL: {}, SMS: {} };

  if (granularControl) {
    const keys = Object.keys(granularControl);

    alertSettings = keys.reduce((accOuter, key) => {
      const values = granularControl[key].reduce((acc, item) => {
        if (item.action !== "All" && !notifySettings[key][item.role]) {
          notifySettings[key][item.role] = item.default.enabled;
        }

        return { ...acc, [item.action]: item.default };
      }, {});
      return { ...accOuter, [key]: { ...values } };
    }, {});
  }

  userInfo.alertSettings = alertSettings;
  userInfo.notify = { ...userInfo.notify, ...notifySettings };

  return userInfo;
}

export function NotificationSelector({
  alertType,
  alertSetting,
  notification,
  disabled,
  webPushAllNotifications,
  member,
  allDisabled,
}: NotificationSelectorType) {
  const dispatch = useDispatch();
  const { wrapRestrictedAction } = useRestrictedSignedProfile();
  const { flags: featureFlags } = useSelector((state: SessionType) => state.myAccount);
  if (!featureFlags) {
    return null;
  }
  const flagKey = alertType === "email" ? "emailFlag" : "smsFlag";
  // should render or not
  const flagName = notification[flagKey];
  if (flagName && !featureFlags[flagName]) {
    return <Blank />;
  }

  if (disabled) {
    return <Blank />;
  }

  if (!alertSetting && alertType !== "web_push") {
    return <Blank />;
  }

  let defaultValue = "off";
  if (alertType === "web_push") {
    if (webPushAllNotifications) {
      defaultValue = "on";
    }
  } else {
    if (alertSetting?.enabled) {
      defaultValue = "on";
    }
    if (alertSetting?.batch) {
      defaultValue = "daily";
    }
  }

  if (alertType === "web_push" && notification.web_push !== "web_push") {
    return uneditableOption(webPushAllNotifications ? "On" : "Off");
  }

  if (!alertSetting?.userModifiableState && alertType !== "web_push") {
    return uneditableOption(alertSetting?.enabled ? "On" : "Off");
  }

  if (allDisabled) {
    return uneditableOption("Off");
  }

  const handleSelectChange = (value: string) => {
    void updateNotification(
      dispatch,
      value,
      alertType.toUpperCase(),
      notification[alertType],
      member
    );
  };

  const options = [
    {
      value: "on",
      label: "On",
      disabled: !(alertType === "web_push" || alertSetting?.userModifiableState),
      "data-testid": "On",
    },
    {
      value: "off",
      label: "Off",
      "data-testid": "Off",
    },
  ];

  if (alertSetting?.userModifiableBatch && alertType !== "web_push") {
    options.push({
      value: "daily",
      label: "Daily",
      "data-testid": "Daily",
    });
  }

  return (
    <div data-testid="notification-container" key={defaultValue}>
      <Dropdown
        defaultValue={defaultValue}
        onClick={wrapRestrictedAction(noop, {
          triggeredBy: "edit notifications",
          triggeredFrom: "My Account",
        })}
        onSelect={handleSelectChange}
        suffixIcon={<DownChevron />}
        disabled={!(alertType === "web_push" || alertSetting?.userModifiableState)}
        style={{
          minWidth: 72,
        }}
        options={options}
      />
    </div>
  );
}

function hasMobilePhone(user: IUser | undefined): boolean {
  return Boolean(user?.phone?.trim());
}

export const Notifications = memo(({ member }: { member?: IUser }) => {
  const {
    user: loggedInUser,
    flags,
    alertSettings,
  } = useSelector((state: SessionType) => state.myAccount);
  const user = member || loggedInUser;
  const { profile } = useSelector((state: SessionType) => state.session);
  const dispatch = useDispatch();
  const featureFlags = useFlags();

  const { data: interviewSettings, isLoading: isLoadingInterviewSettings } =
    useGetInterviewSettings(
      {
        workplaceId: profile?.userId,
      },
      {
        enabled: isDefined(profile),
      }
    );

  useEffect(() => {
    if (flags) {
      return;
    }
    dispatch(setFlags(featureFlags));
  }, [dispatch, featureFlags, flags]);

  useEffect(() => {
    if (!flags) {
      return;
    }
    configureAlertSettings(dispatch, member);
  }, [dispatch, flags, member, user]);

  const rateNegotiationDBStatus = useMemo(() => profile?.rateNegotiation, [profile]);
  const { featureFlagStatus: rateNegotiationFFStatus } = useRateNegotiationFlag({
    isAdmin: false, // does not matter for FF status
    facilityRateNegotiationFlag: rateNegotiationDBStatus,
    facilityId: profile?.userId,
    facilityMsa: profile?.fullAddress?.metropolitanStatisticalArea ?? "",
    facilityState: profile?.fullAddress?.state ?? "",
  });
  const enabledNotifications = useMemo(() => {
    const NotificationValues = Object.values(NotificationDetails).map((preference) => {
      if (member) {
        preference.description = preference.description.replace(" me ", " user ");
      }
      return preference;
    });

    return NotificationValues.filter(({ flag }) => {
      if (!flag) {
        return true;
      }
      // Special handling for rate-negotiation
      // Activation is dependent on the FF as well as the DB
      if (flag === CbhFeatureFlag.RATE_NEGOTIATION_ACTIVATION) {
        return rateNegotiationFFStatus && rateNegotiationDBStatus === RateNegotiationConfigEnum.ON;
      }
      return flags?.[flag];
    });
  }, [flags, member, rateNegotiationDBStatus, rateNegotiationFFStatus]);

  if (!alertSettings || !user) {
    return null;
  }

  const allEmailDisabled = alertSettings.EMAIL["All"]?.default?.enabled === false;
  const allSmsDisabled = alertSettings.SMS["All"]?.default?.enabled === false;
  const smsDisabledNoPhone = !hasMobilePhone(user);

  return (
    <Box>
      {smsDisabledNoPhone && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 8,
            backgroundColor: "#FFF8E1",
            borderRadius: 4,
            color: "#665500",
            fontSize: 14,
          }}
        >
          SMS notifications are disabled because no mobile number is set. Add a mobile number in
          Personal Information to enable SMS notifications.
        </div>
      )}
      <List
        grid={{ column: 1 }}
        dataSource={enabledNotifications.filter(({ email }) => {
          if (
            // this notification is auto-disabled when the facility has enabled interview scheduling
            // see https://github.com/ClipboardHealth/clipboard-health/pull/16563
            email === "candidateApplyEmail" &&
            (isLoadingInterviewSettings || interviewSettings?.scheduleInterviews === true)
          ) {
            return false;
          }
          return true;
        })}
        className="notification-list"
        renderItem={(notification, index) => {
          return (
            <List.Item
              style={{
                marginBottom: 0,
                color: "#868686",
                borderBottom: "1px solid",
                borderBottomColor:
                  index < enabledNotifications.length - 1 ? "#F0F0F0" : "transparent",
              }}
            >
              <Row className="notification-row" style={{ height: 50 }} align={"middle"}>
                <Col span={15} className={"notification-description"}>
                  {notification.description}
                  {!member && <NotificationBlockedMessage notification={notification} />}
                </Col>
                <Col span={3} style={{ textAlign: "center" }}>
                  <NotificationSelector
                    alertType="email"
                    alertSetting={alertSettings.EMAIL[String(notification.email)]?.default}
                    notification={notification}
                    member={member}
                    allDisabled={allEmailDisabled && String(notification.email) !== "All"}
                  />
                </Col>
                <Col span={3} style={{ textAlign: "center" }}>
                  <NotificationSelector
                    alertType="sms"
                    alertSetting={alertSettings.SMS[String(notification.sms)]?.default}
                    notification={notification}
                    member={member}
                    allDisabled={
                      smsDisabledNoPhone || (allSmsDisabled && String(notification.sms) !== "All")
                    }
                  />
                </Col>
                <Col span={3} style={{ textAlign: "center" }}>
                  <NotificationSelector
                    alertType="web_push"
                    notification={notification}
                    disabled={!notification.push}
                    webPushAllNotifications={user?.notify?.WEB_PUSH?.All}
                    member={member}
                  />
                </Col>
              </Row>
            </List.Item>
          );
        }}
      />
      <BatchModal />
    </Box>
  );
});
